import { ServicoInacabado } from './AtividadeCarro';

/**
 * at-99 (ADR 0050) — uma esteira aberta em PARALELO no atendimento.
 *
 * O veículo continua na etapa em que está (a principal); esta é uma esteira onde há serviço em
 * curso ao mesmo tempo. Só as vigentes vêm nas listagens.
 *
 * ⚠️ Não confundir com `servicosInacabados` (at-46): aquilo é serviço que COMEÇOU e parou; isto
 * é serviço acontecendo AGORA numa esteira que não é a etapa do veículo.
 */
export interface EsteiraParalelaDoCarro {
  esteiraId: string;
  nome: string;
  cor: string | null;
  abertaEm: string;
  /** Só na leitura de um veículo (`GET /cars/:id`), não nas listagens. */
  abertaPorNome?: string | null;
  motivo?: string | null;
  paralelaId?: string;
}

/**
 * at-96 — o retorno de garantia (pós-evento): este atendimento é o retorno de um serviço
 * anterior da mesma placa, já entregue.
 *
 * 🔴 NÃO existe campo de garantia nem prazo de 90 dias: a decisão do Victor (09/09/2026) é que a
 * garantia é **só informativa** — a tela mostra há quantos dias o veículo saiu e nada mais.
 *
 * ⚠️ Não confundir com `deliveryForecast`, que é o prazo de PERMANÊNCIA na oficina (também 90
 * dias, contados de outro marco). São dois números iguais com significados diferentes, e essa é
 * a maior fonte de leitura errada desta feature.
 */
export interface PosEventoInfo {
  /**
   * O atendimento de origem — `null` quando o serviço original NÃO ESTÁ NO SISTEMA (at-106).
   *
   * 🔴 Quem consome é obrigado a tratar o nulo: é ele que impede o comparativo com a saída do
   * serviço original de ser oferecido para um serviço que não foi nosso.
   */
  origemCarId: string | null;
  /**
   * 🔴 O discriminador das duas origens, e o único campo que a tela pode usar para escolher o
   * texto. `false` significa **exatamente** "o serviço original não está no sistema" — nunca
   * "foi oficina externa". O sistema não sabe a causa (oficina externa, serviço anterior ao
   * go-live, cadastro que ninguém fez), e afirmá-la na tela seria inventar.
   */
  origemNoSistema: boolean;
  /** Evento do serviço original. Com origem no sistema, derivado da relação — nunca uma cópia. */
  origemEventNumber: number | null;
  /** `null` quando a origem não tem data de entrega registrada — a tela diz isso. Sempre `null` fora do sistema. */
  origemEntregueEm: string | null;
  /** `null` junto com a data acima. NUNCA exibir como "0 dias". */
  diasDesdeEntrega: number | null;
}

export enum CarClass {
  TERCEIRO = 'TERCEIRO',
  ASSOCIADO = 'ASSOCIADO',
  MOBI = 'MOBI',
  CARROS_EXTERNOS = 'CARROS_EXTERNOS',
  CARROS_ATUAL = 'CARROS_ATUAL',
  MOTO = 'MOTO',
  AIRO_CLUBE = 'AIRO_CLUBE',
  PERDA_TOTAL = 'PERDA_TOTAL',
  DIRETORIA = 'DIRETORIA',
}

export interface CarPayload {
  plate: string;
  chassis?: string;
  /** Opcional — só placa e classe são obrigatórias no cadastro (decisão de 07/2026). */
  model?: string;
  /** Opcional — quando ausente, o backend assume a data atual. */
  entryDate?: string;
  /** Opcional — obrigatório apenas para classe ASSOCIADO (validado no backend). */
  eventNumber?: number;
  /** Protocolo Hinova do evento — persistido no Evento, não no carro. */
  protocol?: string;
  carClass: CarClass;
  damageType?: string;
  daysInShop?: number;
  statusUpdatedAt?: string;
  authorizedAt?: string;
  observation?: string;
  color?: string;
  modelYear?: number;
  anoFabricacao?: number;
  marca?: string;
  fipeBrandCode?: string;
  fipeModelCode?: string;
  fipeYearCode?: string;
  renavam?: string;
  /** at-15: valor FIPE informado pelo SGA no cadastro por evento — piso da perda total sem códigos FIPE. */
  valorFipeSga?: number;
  numeroBo?: string;
  /** Nome completo do proprietário (associado ou terceiro). */
  ownerName?: string;
  /** Telefone do proprietário — chave de verificação do link público de checklist. */
  ownerPhone?: string;
  /** IMEI do rastreador — única chave que localiza o equipamento na rastreadora (at-76). */
  rastreadorImei?: string;
  /** at-54: relatos e combinados com o cliente. Até 10.000 caracteres (at-58). */
  observacoesCliente?: string;
  /** at-54: anotações internas da equipe. Até 10.000 caracteres (at-58). */
  observacoesOficina?: string;
  /**
   * at-96: id do atendimento de origem quando este cadastro é um RETORNO DE GARANTIA.
   *
   * ⚠️ Amarra de deploy: o backend precisa subir ANTES do frontend — o `ValidationPipe` tem
   * `forbidNonWhitelisted`, então o backend velho devolve 400 na tela de cadastro inteira, não
   * "campo ignorado".
   */
  posEventoDeCarId?: string;
  /**
   * at-106: o número do evento do serviço ORIGINAL quando ele não está no sistema — o veículo
   * volta em garantia, mas o serviço que ele vem avaliar nunca virou atendimento aqui.
   *
   * 🔴 MUTUAMENTE EXCLUSIVO com `posEventoDeCarId` (o backend recusa os dois juntos), e nunca é
   * o mesmo número de `eventNumber`, que é o evento NOVO deste retorno.
   */
  posEventoDeEventoOrigemNumero?: number;
}

export interface Car {
  id: string;
  plate: string;
  chassis?: string;
  model: string;
  entryDate: Date;
  eventNumber: number;
  carClass: 'TERCEIRO' | 'ASSOCIADO' | 'MOBI' | 'CARROS_EXTERNOS' | 'CARROS_ATUAL' | 'MOTO' | 'AIRO_CLUBE' | 'PERDA_TOTAL' | 'DIRETORIA' ;
  damageType?: 'PEQUENO' | 'MEDIO' | 'GRANDE';
  daysInShop?: number;
  authorizedAt?: Date;
  observation?: string;
  /** at-54: relatos e combinados com o cliente. */
  observacoesCliente?: string | null;
  /** at-54: anotações internas da equipe. */
  observacoesOficina?: string | null;
  status: string;
  statusUpdatedAt?: Date;
  pendingStatus?: string;
  esteiraId?: string;
  /**
   * ⚠️ `sistema` decide se a etapa aceita esteira em paralelo (at-99): em etapa de fluxo livre o
   * backend recusa. O campo esteve AUSENTE do `GET /cars/:id` até a at-99, e `undefined === false`
   * escondia o botão em silêncio — por isso ele é declarado aqui, e não inferido da chave.
   */
  esteira?: { id: string; chave: string; nome: string; cor: string; ordem: number; sistema?: boolean; };
  pendingEsteiraId?: string;
  pendingEsteira?: { id: string; chave: string; nome: string; cor: string; };
  pendingStatusUpdatedAt?: Date;
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  color?: string;
  protocol?: string;
  /** @deprecated at-45: use `colaboradores`. Mantido como compatibilidade (1º vinculado). */
  responsibleId?: string;
  /** Colaboradores vinculados ao veículo (at-45) — N por veículo. */
  colaboradores?: Array<{ id: string; fullName: string | null }>;
  budgetStartDate?: Date;
  budgetEndDate?: Date;
  deliveryForecast?: number;
  firstApprovalAt?: Date;
  estimatedDaysInStatus?: number;
  daysToAuthorize?: number;
  releasedAt?: Date;
  releaseNote?: string;
  releasedById?: string;
  releasedBy?: { id: string; fullName: string } | null;
  releaseStatus?: 'TOTAL' | 'PARCIAL' | 'PENDENTE';
  esteirasLiberadas?: Array<{
    esteiraId: string;
    nome: string;
    cor?: string;
    releasedAt?: string;
    releasedBy?: { id: string; fullName: string } | null;
  }>;
  returnedToWaiting?: boolean;
  /** at-46: serviços em aberto (inacabados ou em andamento) por esteira. */
  servicosInacabados?: ServicoInacabado[];
  /** at-99: esteiras abertas em paralelo agora — o selo ao lado da placa. */
  esteirasParalelas?: EsteiraParalelaDoCarro[];
  /** at-96: preenchido ⇒ este atendimento é um retorno de garantia. Ver `PosEventoInfo`. */
  posEvento?: PosEventoInfo | null;
  /** at-135: o semáforo do prazo da etapa (só em `GET /cars/:id`; nulo em atendimento encerrado). */
  situacaoPrazo?: 'DENTRO' | 'EM_RISCO' | 'ESTOURADO' | 'SEM_PRAZO' | null;
  /** at-135: dias inteiros na etapa atual. */
  diasNaEtapa?: number | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface CarFilter {
  id: string;
  /** at-96: preenchido ⇒ retorno de garantia (o selo na listagem sai daqui). */
  posEvento?: PosEventoInfo | null;
  /**
   * at-96: a FAMÍLIA do último checklist do atendimento — "foi de entrada ou de saída?".
   * O backend colapsa os tipos de pós-evento na família, porque os nomes novos não existem no
   * enum legado `TipoServico`. Quem é pós-evento se sabe pelo campo `posEvento`.
   */
  tipoServico?: 'ENTRADA' | 'SAIDA' | null;
  plate: string;
  chassis: string;
  model: string;
  entryDate: string;
  eventNumber: number;
  carClass: string;
  damageType: string;
  daysInShop: number;
  authorizedAt: string | null;
  observation: string | null;
  status: string;
  statusUpdatedAt: string;
  pendingStatus: string | null;
  esteiraId?: string;
  esteira?: { id: string; chave: string; nome: string; cor: string; ordem: number; };
  pendingEsteiraId?: string;
  pendingEsteira?: { id: string; chave: string; nome: string; cor: string; };
  pendingStatusUpdatedAt: string | null;
  approvalStatus: string;
  color: string;
  protocol: string | null;
  responsibleId: string;
  budgetStartDate: string | null;
  budgetEndDate: string | null;
  deliveryForecast: number;
  firstApprovalAt: string | null;
  estimatedDaysInStatus: number | null;
  daysToAuthorize: number | null;
  releasedAt?: string | null;
  releaseNote?: string | null;
  releasedById?: string | null;
  releasedBy?: { id: string; fullName: string } | null;
  releaseStatus?: 'TOTAL' | 'PARCIAL' | 'PENDENTE';
  esteirasLiberadas?: Array<{
    esteiraId: string;
    nome: string;
    cor?: string;
    releasedAt?: string;
    releasedBy?: { id: string; fullName: string } | null;
  }>;
  returnedToWaiting?: boolean;
  /** at-46: serviços em aberto (inacabados ou em andamento) por esteira. */
  servicosInacabados?: ServicoInacabado[];
  /** at-99: esteiras abertas em paralelo agora — o selo ao lado da placa e o chip do card. */
  esteirasParalelas?: EsteiraParalelaDoCarro[];
  createdAt: string;
  updatedAt: string;
  partsSituation?: {
    color: 'green' | 'yellow' | 'red' | null;
    total: number;
    received: number;
    pending: number;
    overdue: number;
  };
}

export interface FilterCarsResponse {
  total: number;
  page: number;
  limit: number;
  cars: CarFilter[];
}

export interface CarTag {
  definicaoId: string;
  nome: string;
  valor: string | null;
}

export interface Filters {
  carClass?: CarClass[];
  carStatus?: string[];
  pendingStatus?: string[];
  approvalStatus?: string;
  damageType?: string[];
}

export interface BudgetPayload {
  budgetStartDate?: string;
  budgetEndDate?: string;
}
